import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  AreaChart, Area, ComposedChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine,
} from 'recharts';
import { useSiteActions, actionMarkers, groupMarkersByDate, ActionTooltipRows, QuickActionModal } from './SiteActionsOverlay';
import gscApi from '../services/search-console';
import './GoogleAnalytics.css'; // réutilise ga-kpi, ga-panel, ga-table, ga-grid-2…
import './GoogleAds.css';       // réutilise gads-audit-*, gads-reco-*, gads-prio-*…
import './SearchConsole.css';

// ── Utils dates ────────────────────────────────────────────────────────────────
// Search Console publie ses données avec ~2-3 jours de retard. On décale donc la
// référence « aujourd'hui » pour éviter des journées vides en fin de période.
const DATA_LAG_DAYS = 3;

function toISODate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function computeRange(preset) {
  const now = new Date();
  const ref = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  ref.setDate(ref.getDate() - DATA_LAG_DAYS); // dernier jour avec données
  const back = (n) => { const d = new Date(ref); d.setDate(d.getDate() - n); return d; };
  switch (preset) {
    case '7j':   return { start: toISODate(back(6)),  end: toISODate(ref) };
    case '28j':  return { start: toISODate(back(27)), end: toISODate(ref) };
    case '90j':  return { start: toISODate(back(89)), end: toISODate(ref) };
    case '6m':   return { start: toISODate(back(179)), end: toISODate(ref) };
    case '12m':  return { start: toISODate(back(364)), end: toISODate(ref) };
    default:     return { start: toISODate(back(27)), end: toISODate(ref) };
  }
}

const PRESETS = [
  { id: '7j',     label: '7 jours' },
  { id: '28j',    label: '28 jours' },
  { id: '90j',    label: '3 mois' },
  { id: '6m',     label: '6 mois' },
  { id: '12m',    label: '12 mois' },
  { id: 'custom', label: 'Personnalisé' },
];

// ── Utils format ───────────────────────────────────────────────────────────────

const nf = new Intl.NumberFormat('fr-FR');
const nf1 = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 1 });
const nf2 = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 2 });

function fmtNumber(n) { return nf.format(Math.round(n || 0)); }
function fmtCompact(n) {
  n = n || 0;
  if (Math.abs(n) >= 1000000) return nf1.format(n / 1000000) + ' M';
  if (Math.abs(n) >= 1000) return nf1.format(n / 1000) + ' k';
  return nf.format(Math.round(n));
}
function fmtPercent(n) { return nf1.format((n || 0) * 100) + ' %'; }
function fmtPercent2(n) { return nf2.format((n || 0) * 100) + ' %'; }
function fmtPosition(n) { return n ? nf1.format(n) : '—'; }
function fmtDelta(d) {
  if (d == null) return '—';
  const pct = d * 100;
  const sign = pct > 0 ? '+' : '';
  return `${sign}${nf1.format(pct)} %`;
}
// '20260612' → '12/06'
function fmtDay(s) {
  if (!s || s.length !== 8) return s;
  return `${s.slice(6, 8)}/${s.slice(4, 6)}`;
}

// « https://www.domimmo.com/ » → « domimmo.com » ; « sc-domain:domimmo.com » → « domimmo.com »
function prettySite(url) {
  if (!url) return '';
  if (url.startsWith('sc-domain:')) return url.slice('sc-domain:'.length);
  return url.replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/$/, '');
}
// Pour une page : ne garder que le chemin (l'origine est implicite, un seul site).
function prettyPath(url) {
  if (!url) return '/';
  try { const u = new URL(url); return (u.pathname + u.search) || '/'; }
  catch { return url.replace(/^https?:\/\/[^/]+/, '') || '/'; }
}

// ── Config KPIs ──────────────────────────────────────────────────────────────

const KPI_DEFS = [
  { key: 'clicks',      label: 'Clics',         fmt: fmtNumber,  series: 'clicks',      hint: 'Visites depuis Google' },
  { key: 'impressions', label: 'Impressions',   fmt: fmtNumber,  series: 'impressions', hint: 'Apparitions dans les résultats' },
  { key: 'ctr',         label: 'CTR',           fmt: fmtPercent, series: 'ctr',         hint: 'Taux de clic moyen' },
  { key: 'position',    label: 'Position moy.', fmt: fmtPosition, series: 'position',    hint: 'Rang moyen dans Google', lowerIsBetter: true },
];

const MAIN_METRICS = [
  { key: 'clicks',      label: 'Clics',         color: '#0891b2', fmt: fmtNumber },
  { key: 'impressions', label: 'Impressions',   color: '#3b82f6', fmt: fmtNumber },
  { key: 'ctr',         label: 'CTR',           color: '#f59e0b', fmt: fmtPercent, axis: 'pct' },
  { key: 'position',    label: 'Position moy.', color: '#a855f7', fmt: fmtPosition, axis: 'pos', reversed: true },
];

const PALETTE = ['#0891b2', '#3b82f6', '#f59e0b', '#22c55e', '#a855f7', '#ef4444', '#14b8a6', '#ec4899'];

const LS_SITE = 'crm-gsc-site';
const LS_PRESET = 'crm-gsc-preset';

// ── Composants atomiques ─────────────────────────────────────────────────────

function KpiCard({ def, kpi, chartData }) {
  if (!kpi) return null;
  const positive = def.lowerIsBetter ? (kpi.delta != null && kpi.delta < 0) : (kpi.delta != null && kpi.delta > 0);
  const negative = def.lowerIsBetter ? (kpi.delta != null && kpi.delta > 0) : (kpi.delta != null && kpi.delta < 0);
  const deltaClass = kpi.delta == null ? 'flat' : positive ? 'up' : negative ? 'down' : 'flat';
  const spark = def.series && chartData && chartData.length > 1
    ? chartData.map(p => ({ v: p[def.series] || 0 })) : null;
  return (
    <div className="ga-kpi">
      <div className="ga-kpi-label" title={def.hint}>{def.label}</div>
      <div className="ga-kpi-value">{def.fmt(kpi.value)}</div>
      <div className="ga-kpi-foot">
        <span className={`ga-delta ${deltaClass}`}>
          {kpi.delta != null && (positive ? '▲' : negative ? '▼' : '')} {fmtDelta(kpi.delta)}
        </span>
        {spark && (
          <span className="ga-spark">
            <ResponsiveContainer width="100%" height={28}>
              <AreaChart data={spark} margin={{ top: 2, right: 0, left: 0, bottom: 0 }}>
                <Area type="monotone" dataKey="v" stroke="var(--color-accent)" fill="var(--color-accent-light)" strokeWidth={1.5} dot={false} isAnimationActive={false} />
              </AreaChart>
            </ResponsiveContainer>
          </span>
        )}
      </div>
    </div>
  );
}

function Panel({ title, subtitle, children, className = '' }) {
  return (
    <div className={`ga-panel ${className}`}>
      <div className="ga-panel-head">
        <h3>{title}</h3>
        {subtitle && <span className="ga-panel-sub">{subtitle}</span>}
      </div>
      {children}
    </div>
  );
}

// Barre de position : la pastille colore selon la qualité du rang.
function posClass(p) {
  if (!p) return '';
  if (p <= 3) return 'gsc-pos-top';
  if (p <= 10) return 'gsc-pos-good';
  if (p <= 20) return 'gsc-pos-mid';
  return 'gsc-pos-low';
}

function PerfTable({ rows, labelHead, isPage, empty = 'Aucune donnée' }) {
  if (!rows || rows.length === 0) return <div className="ga-empty">{empty}</div>;
  return (
    <table className="ga-table gsc-table">
      <thead>
        <tr>
          <th>{labelHead}</th>
          <th className="num">Clics</th>
          <th className="num">Impr.</th>
          <th className="num">CTR</th>
          <th className="num">Position</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i}>
            <td className="ga-td-label" title={r.label}>
              {isPage
                ? <a href={r.label} target="_blank" rel="noreferrer" className="gsc-link">{prettyPath(r.label)}</a>
                : r.label}
            </td>
            <td className="num"><strong>{fmtNumber(r.clicks)}</strong></td>
            <td className="num">{fmtNumber(r.impressions)}</td>
            <td className="num">{fmtPercent2(r.ctr)}</td>
            <td className="num"><span className={`gsc-pos ${posClass(r.position)}`}>{fmtPosition(r.position)}</span></td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function DonutChart({ data, dataKey, height = 240 }) {
  const total = (data || []).reduce((s, d) => s + (d[dataKey] || 0), 0);
  if (!data || data.length === 0) return <div className="ga-empty">Aucune donnée</div>;
  return (
    <div className="ga-donut-wrap">
      <ResponsiveContainer width="100%" height={height}>
        <PieChart>
          <Pie data={data} dataKey={dataKey} nameKey="label" cx="50%" cy="50%"
            innerRadius={55} outerRadius={85} paddingAngle={2} stroke="var(--bg-surface)" strokeWidth={2}>
            {data.map((d, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
          </Pie>
          <Tooltip content={<DonutTooltip total={total} />} />
        </PieChart>
      </ResponsiveContainer>
      <div className="ga-legend">
        {data.slice(0, 8).map((d, i) => (
          <div className="ga-legend-row" key={i}>
            <span className="ga-legend-dot" style={{ background: PALETTE[i % PALETTE.length] }} />
            <span className="ga-legend-label" title={d.label}>{d.label}</span>
            <span className="ga-legend-val">{total ? Math.round((d[dataKey] / total) * 100) : 0} %</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function DonutTooltip({ active, payload, total }) {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0];
  return (
    <div className="ga-tooltip">
      <div className="ga-tt-title">{p.name}</div>
      <div>{fmtNumber(p.value)} ({total ? Math.round((p.value / total) * 100) : 0} %)</div>
    </div>
  );
}

function MainChartTooltip({ active, payload, label, metricDef, compare, actionsByDate }) {
  if (!active || !payload || !payload.length) return null;
  const cur = payload.find(p => p.dataKey === 'value');
  const prev = payload.find(p => p.dataKey === 'prev');
  return (
    <div className="ga-tooltip">
      <div className="ga-tt-title">{fmtDay(label)}</div>
      <div><span className="ga-tt-dot" style={{ background: metricDef.color }} />{metricDef.label} : <b>{metricDef.fmt(cur?.value)}</b></div>
      {compare && prev && prev.value != null && (
        <div className="muted"><span className="ga-tt-dot" style={{ background: 'var(--text-muted)' }} />Période préc. : {metricDef.fmt(prev.value)}</div>
      )}
      <ActionTooltipRows items={actionsByDate && actionsByDate[label]} />
    </div>
  );
}

// ── Onglet Audit IA SEO ───────────────────────────────────────────────────────

function gscScoreColor(s) {
  if (s == null || Number.isNaN(s)) return 'var(--text-muted)';
  if (s >= 70) return 'var(--color-success)';
  if (s >= 45) return 'var(--color-warning)';
  return 'var(--color-error)';
}
const GSC_DIM_LABELS = {
  visibilite: 'Visibilité & croissance', ctr: 'CTR & extraits enrichis',
  contenu: 'Contenu & mots-clés', pages: 'Pages & structure', technique: 'Technique & indexation',
  structure_donnees: 'Données structurées & GEO',
};
const GSC_PRIO = { P1: { label: 'Prioritaire', cls: 'gads-prio-p1' }, P2: { label: 'Important', cls: 'gads-prio-p2' }, P3: { label: 'Optimisation', cls: 'gads-prio-p3' } };
const GSC_EFFORT = { faible: 'gads-eff-low', moyen: 'gads-eff-mid', 'élevé': 'gads-eff-high', 'élevée': 'gads-eff-high' };

// Champ « livrable » copiable (titre / méta réécrits par l'IA).
function CopyField({ label, value, max, mono }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(value).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); }).catch(() => {});
  };
  const len = (value || '').length;
  const over = max && len > max;
  return (
    <div className="gsc-deliv-field">
      <div className="gsc-deliv-top">
        <span className="gsc-deliv-label">{label}</span>
        {max && <span className={`gsc-deliv-count ${over ? 'over' : ''}`}>{len}/{max}</span>}
        <button className="gsc-copy-btn" onClick={copy}>{copied ? '✓ copié' : '⧉ copier'}</button>
      </div>
      {mono
        ? <pre className="gsc-deliv-value" style={{ whiteSpace: 'pre-wrap', overflowX: 'auto', fontFamily: 'monospace', fontSize: 12, margin: 0, lineHeight: 1.45 }}>{value}</pre>
        : <div className="gsc-deliv-value">{value}</div>}
    </div>
  );
}

function GscAuditTab({ site, siteLabel }) {
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);
  const pollRef = useRef(null);

  const fetchAudit = useCallback(async () => {
    if (!site) return null;
    try { const { data } = await gscApi.getAudit(site); setAudit(data); return data; }
    catch (e) { setError(e.response?.data?.error || e.message); return null; }
  }, [site]);

  const startPolling = useCallback(() => {
    setRunning(true);
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      const d = await fetchAudit();
      if (d && d.status !== 'running') { clearInterval(pollRef.current); setRunning(false); }
    }, 5000);
  }, [fetchAudit]);

  useEffect(() => {
    setAudit(null); setError(null); setRunning(false); setLoading(true);
    fetchAudit().then(d => { setLoading(false); if (d && d.status === 'running') startPolling(); });
    return () => clearInterval(pollRef.current);
  }, [site, fetchAudit, startPolling]);

  const launch = async () => {
    setError(null); setRunning(true);
    try { await gscApi.runAudit(site); startPolling(); }
    catch (e) { setError(e.response?.data?.error || e.message); setRunning(false); }
  };

  if (!site) return <div className="gads-loading">Sélectionnez un site.</div>;
  if (loading) return <div className="gads-loading">Chargement de l'audit…</div>;

  const done = audit && audit.status === 'done';
  const isRunning = running || (audit && audit.status === 'running');
  const recos = (done && audit.recommendations) || [];
  const quickWins = recos.filter(r => r.is_quick_win);
  const scores = (done && audit.scores) || [];

  return (
    <div className="gads-audit">
      <div className="gads-audit-head">
        <div>
          <h3>🤖 Audit IA — référencement naturel (SEO)</h3>
          <p className="muted">Analyse des données Search Console (visibilité, CTR, contenu, pages) + livrables prêts à appliquer · {siteLabel || 'site'} · lecture seule</p>
        </div>
        <div className="gads-ap-head-actions">
          <button className="page-action-btn" onClick={launch} disabled={isRunning}>
            {isRunning ? '⏳ Audit en cours…' : (done ? "↻ Relancer l'audit" : "▶ Lancer l'audit")}
          </button>
        </div>
      </div>

      {error && <div className="gads-error">⚠️ {error}</div>}
      {isRunning && <div className="gads-audit-running">L'IA analyse votre référencement… ~1 minute. Vous pouvez quitter et revenir.</div>}
      {audit && audit.status === 'error' && !isRunning && <div className="gads-error">L'audit a échoué : {audit.error || 'erreur inconnue'}.</div>}
      {!audit && !isRunning && (
        <div className="gads-audit-empty"><p>Aucun audit pour ce site. Lancez une analyse SEO : visibilité, taux de clic, contenu, pages, opportunités, avec des titres et méta-descriptions réécrits prêts à copier.</p></div>
      )}

      {done && (
        <>
          <div className="gads-audit-summary">
            <div className="gads-audit-score" style={{ borderColor: gscScoreColor(Number(audit.global_score)) }}>
              <div className="gads-audit-score-val" style={{ color: gscScoreColor(Number(audit.global_score)) }}>{Math.round(Number(audit.global_score))}</div>
              <div className="gads-audit-score-max">/ 100</div>
            </div>
            <div className="gads-audit-verdict">
              <p>{audit.verdict}</p>
              <div className="muted gads-audit-date">Audit du {new Date(audit.run_at).toLocaleString('fr-FR')} · {audit.model} · 28 derniers jours</div>
            </div>
          </div>

          {quickWins.length > 0 && (
            <Panel title="⚡ Quick wins" subtitle="Fort impact, faible effort — à faire en premier">
              <div className="gads-qw-grid">
                {quickWins.map((r, i) => <div key={i} className="gads-qw-card"><strong>{r.title}</strong><span className="muted">{r.action}</span></div>)}
              </div>
            </Panel>
          )}

          <Panel title="Scores par dimension">
            <div className="gads-dim-grid">
              {scores.map((s, i) => (
                <div key={i} className="gads-dim">
                  <div className="gads-dim-top"><span>{GSC_DIM_LABELS[s.dimension] || s.dimension}</span><strong style={{ color: gscScoreColor(Number(s.score)) }}>{Math.round(Number(s.score))}</strong></div>
                  <div className="gads-dim-bar"><div style={{ width: `${Math.max(0, Math.min(100, Number(s.score)))}%`, background: gscScoreColor(Number(s.score)) }} /></div>
                  <p className="muted">{s.summary}</p>
                </div>
              ))}
            </div>
          </Panel>

          <Panel title="Recommandations" subtitle={`${recos.length} action(s) classée(s) par priorité — livrables prêts à copier`}>
            <div className="gads-reco-list">
              {recos.map((r, i) => {
                const prio = GSC_PRIO[r.priority] || { label: r.priority, cls: 'gads-prio-p3' };
                const hasDeliverable = (r.proposed_title && r.proposed_title.trim()) || (r.proposed_meta && r.proposed_meta.trim()) || (r.content_brief && r.content_brief.trim()) || (r.proposed_schema && r.proposed_schema.trim());
                return (
                  <div key={i} className="gads-reco">
                    <div className="gads-reco-head">
                      <span className={`gads-prio ${prio.cls}`}>{prio.label}</span>
                      <strong>{r.title}</strong>
                      {r.is_quick_win && <span className="gads-qw-tag">⚡ quick win</span>}
                      {r.effort && <span className={`gads-eff ${GSC_EFFORT[String(r.effort).toLowerCase()] || ''}`}>effort {r.effort}</span>}
                    </div>
                    {r.target && <p className="gsc-reco-target">🎯 {r.target}</p>}
                    {r.finding && <p className="gads-reco-finding">📊 {r.finding}</p>}
                    {r.why && <p className="gads-reco-why">{r.why}</p>}
                    {r.action && <p className="gads-reco-action">✅ <strong>À faire :</strong> {r.action}</p>}
                    {r.impact && <p className="gads-reco-impact">📈 Impact : {r.impact}</p>}
                    {hasDeliverable && (
                      <div className="gsc-deliverable">
                        <div className="gsc-deliv-head">🛠️ Livrable prêt à appliquer</div>
                        {r.proposed_title && r.proposed_title.trim() && <CopyField label="Balise titre (&lt;title&gt;)" value={r.proposed_title} max={60} />}
                        {r.proposed_meta && r.proposed_meta.trim() && <CopyField label="Méta-description" value={r.proposed_meta} max={155} />}
                        {r.content_brief && r.content_brief.trim() && (
                          <div className="gsc-deliv-field">
                            <div className="gsc-deliv-top"><span className="gsc-deliv-label">Brief de contenu</span></div>
                            <div className="gsc-deliv-brief">{r.content_brief}</div>
                          </div>
                        )}
                        {r.proposed_schema && r.proposed_schema.trim() && <CopyField label="Données structurées (JSON-LD)" value={r.proposed_schema} mono />}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </Panel>

          {audit.report_md && (
            <Panel title="Rapport détaillé">
              <details className="gads-report"><summary>Afficher le rapport complet</summary><div className="gads-report-md">{audit.report_md}</div></details>
            </Panel>
          )}
        </>
      )}
    </div>
  );
}

// ── Composant principal ──────────────────────────────────────────────────────

function SearchConsole({ user, setHeaderAction }) {
  const [sites, setSites] = useState([]);
  const [configured, setConfigured] = useState(true);
  const [sitesError, setSitesError] = useState(null);

  const [site, setSite] = useState(() => localStorage.getItem(LS_SITE) || '');
  const [preset, setPreset] = useState(() => localStorage.getItem(LS_PRESET) || '28j');
  const [custom, setCustom] = useState(() => computeRange('28j'));
  const [compare, setCompare] = useState(true);

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [tab, setTab] = useState('overview');
  const [mainMetric, setMainMetric] = useState('clicks');

  const [overview, setOverview] = useState(null);
  const [ovLoading, setOvLoading] = useState(false);

  const range = useMemo(
    () => (preset === 'custom' ? custom : computeRange(preset)),
    [preset, custom]
  );

  // Page en lecture seule : aucune action de header.
  useEffect(() => { if (setHeaderAction) setHeaderAction(null); }, [setHeaderAction]);

  // ── Chargement des sites ──
  useEffect(() => {
    let alive = true;
    gscApi.getSites()
      .then(({ data }) => {
        if (!alive) return;
        setConfigured(data.configured);
        const list = data.sites || [];
        setSites(list);
        if (list.length && (!site || !list.some(s => s.url === site))) {
          setSite(list[0].url);
        }
      })
      .catch(err => alive && setSitesError(err.response?.data?.error || err.message));
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Chargement du rapport ──
  const loadReport = useCallback(() => {
    if (!site) return;
    setLoading(true);
    setError(null);
    gscApi.getReport({ site, start: range.start, end: range.end, compare })
      .then(({ data }) => setReport(data))
      .catch(err => { setError(err.response?.data?.error || err.message); setReport(null); })
      .finally(() => setLoading(false));
  }, [site, range.start, range.end, compare]);

  useEffect(() => { if (tab !== 'sites' && tab !== 'audit') loadReport(); }, [loadReport, tab]);

  // ── Chargement de la vue multi-sites ──
  useEffect(() => {
    if (tab !== 'sites') return;
    setOvLoading(true);
    gscApi.getOverview({ start: range.start, end: range.end })
      .then(({ data }) => setOverview(data))
      .catch(() => setOverview(null))
      .finally(() => setOvLoading(false));
  }, [tab, range.start, range.end]);

  // ── Persistance ──
  useEffect(() => { if (site) localStorage.setItem(LS_SITE, site); }, [site]);
  useEffect(() => { localStorage.setItem(LS_PRESET, preset); }, [preset]);

  // ── Données du graphe principal ──
  const metricDef = MAIN_METRICS.find(m => m.key === mainMetric) || MAIN_METRICS[0];
  const [actionsRefresh, setActionsRefresh] = useState(0);
  const [showQuickAction, setShowQuickAction] = useState(false);
  const { actions: siteActions, siteId: actionSiteId, siteName: actionSiteName } =
    useSiteActions('gsc', site, range.start, range.end, !!site, actionsRefresh);
  const mainChartData = useMemo(() => {
    if (!report?.timeseries) return [];
    const prev = report.previousTimeseries || [];
    return report.timeseries.map((p, i) => ({
      date: p.date,
      value: p[mainMetric] || 0,
      prev: prev[i] ? (prev[i][mainMetric] != null ? prev[i][mainMetric] : null) : null,
    }));
  }, [report, mainMetric]);

  // ── Rendu : non configuré ──
  if (configured === false) {
    return (
      <div className="page-container">
        <div className="ga-config-banner">
          <h2>🔎 Google Search Console</h2>
          <p>Le connecteur Google Search Console n'est pas configuré sur ce serveur.</p>
          <p className="muted">Renseigner la clé du compte de service (<code>GSC_SA_KEY_FILE</code> / <code>GSC_SA_KEY_JSON</code>, ou la clé GA4 existante) dans le <code>.env</code> du backend, puis autoriser ce compte dans Search Console.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container ga-page gsc-page">
      {/* 1. Onglets */}
      <div className="page-tabs">
        <button className={tab === 'overview' ? 'active' : ''} onClick={() => setTab('overview')}>📊 Vue d'ensemble</button>
        <button className={tab === 'queries' ? 'active' : ''} onClick={() => setTab('queries')}>🔑 Requêtes</button>
        <button className={tab === 'pages' ? 'active' : ''} onClick={() => setTab('pages')}>📄 Pages</button>
        <button className={tab === 'opportunities' ? 'active' : ''} onClick={() => setTab('opportunities')}>💡 Opportunités</button>
        <button className={tab === 'sites' ? 'active' : ''} onClick={() => setTab('sites')}>🌐 Tous les sites</button>
        <button className={tab === 'audit' ? 'active' : ''} onClick={() => setTab('audit')}>🤖 Audit IA</button>
      </div>

      {/* 2. Toolbar — sélection site + période */}
      <div className="page-toolbar ga-toolbar">
        {tab !== 'sites' && (
          <select className="page-select ga-prop-select" value={site} onChange={e => setSite(e.target.value)}>
            {sites.map(s => <option key={s.url} value={s.url}>{prettySite(s.url)}</option>)}
          </select>
        )}

        {tab !== 'audit' && (
          <div className="ga-presets">
            {PRESETS.map(p => (
              <button key={p.id}
                className={`ga-preset ${preset === p.id ? 'active' : ''}`}
                onClick={() => setPreset(p.id)}>{p.label}</button>
            ))}
          </div>
        )}

        {tab !== 'audit' && preset === 'custom' && (
          <div className="ga-custom-dates">
            <input type="date" className="page-select" value={custom.start} max={custom.end}
              onChange={e => setCustom(c => ({ ...c, start: e.target.value }))} />
            <span>→</span>
            <input type="date" className="page-select" value={custom.end} min={custom.start}
              onChange={e => setCustom(c => ({ ...c, end: e.target.value }))} />
          </div>
        )}

        {tab !== 'sites' && tab !== 'audit' && (
          <>
            <button className={`ga-toggle ${compare ? 'active' : ''}`} onClick={() => setCompare(c => !c)}
              title="Comparer à la période précédente">⇄ Comparer</button>
            <button className="ga-icon-btn" onClick={loadReport} title="Rafraîchir">↻</button>
          </>
        )}
      </div>

      {/* Bandeau contextuel */}
      <div className="ga-context">
        <span className="ga-context-site">{tab === 'sites' ? 'Tous les sites' : (prettySite(site) || '—')}</span>
        <span className="ga-context-range">
          {tab === 'audit' ? '28 derniers jours · audit SEO' : (
            <>
              {range.start} → {range.end}
              {tab !== 'sites' && compare && report?.previous && <span className="muted"> · vs {report.previous.start} → {report.previous.end}</span>}
            </>
          )}
        </span>
        <span className="muted ga-context-tz">🕓 données à J-{DATA_LAG_DAYS}</span>
      </div>

      {sitesError && <div className="ga-error">⚠️ {sitesError}</div>}
      {error && <div className="ga-error">⚠️ {error}</div>}
      {report?.warnings?.length > 0 && (
        <div className="ga-warn">Certaines sections n'ont pas pu être chargées : {report.warnings.join(', ')}.</div>
      )}

      {loading && !report && tab !== 'sites' && tab !== 'audit' && <div className="ga-loading">Chargement des données Google Search Console…</div>}

      {/* ── AUDIT IA SEO ── */}
      {tab === 'audit' && <GscAuditTab site={site} siteLabel={prettySite(site)} />}

      {/* ── TOUS LES SITES ── */}
      {tab === 'sites' && (
        <Panel title="Performance par site" subtitle="Clics, impressions, CTR et position moyenne sur la période">
          {ovLoading && !overview ? <div className="ga-loading">Chargement…</div> : (
            <table className="ga-table gsc-table">
              <thead>
                <tr>
                  <th>Site</th>
                  <th className="num">Clics</th>
                  <th className="num">Impressions</th>
                  <th className="num">CTR</th>
                  <th className="num">Position moy.</th>
                </tr>
              </thead>
              <tbody>
                {(overview?.sites || []).map((s, i) => (
                  <tr key={i} className="gsc-site-row" onClick={() => { setSite(s.url); setTab('overview'); }} title="Voir le détail">
                    <td className="ga-td-label">{prettySite(s.url)}{s.error && <span className="muted"> · indisponible</span>}</td>
                    <td className="num"><strong>{fmtNumber(s.clicks)}</strong></td>
                    <td className="num">{fmtNumber(s.impressions)}</td>
                    <td className="num">{fmtPercent2(s.ctr)}</td>
                    <td className="num"><span className={`gsc-pos ${posClass(s.position)}`}>{fmtPosition(s.position)}</span></td>
                  </tr>
                ))}
                {overview && overview.sites.length === 0 && (
                  <tr><td colSpan={5} className="ga-empty">Aucun site accessible.</td></tr>
                )}
              </tbody>
            </table>
          )}
        </Panel>
      )}

      {tab !== 'sites' && report && (
        <>
          {/* ── VUE D'ENSEMBLE ── */}
          {tab === 'overview' && (
            <>
              <div className="ga-kpi-grid gsc-kpi-grid">
                {KPI_DEFS.map(def => (
                  <KpiCard key={def.key} def={def} kpi={report.kpis[def.key]} chartData={report.timeseries} />
                ))}
              </div>

              <Panel
                title="Évolution dans le temps"
                subtitle={compare ? 'Trait plein = période courante · pointillé = période précédente' : null}
              >
                <div className="ga-metric-switch">
                  {MAIN_METRICS.map(m => (
                    <button key={m.key}
                      className={`ga-chip ${mainMetric === m.key ? 'active' : ''}`}
                      style={mainMetric === m.key ? { borderColor: m.color, color: m.color } : null}
                      onClick={() => setMainMetric(m.key)}>{m.label}</button>
                  ))}
                  <button className="ga-chip" title="Consigner une action menée sur ce site" style={{ marginLeft: 'auto' }} onClick={() => setShowQuickAction(true)}>+ Action</button>
                </div>
                <ResponsiveContainer width="100%" height={300}>
                  <ComposedChart data={mainChartData} margin={{ top: 10, right: 16, left: 4, bottom: 0 }}>
                    <defs>
                      <linearGradient id="gscMainFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={metricDef.color} stopOpacity={0.35} />
                        <stop offset="100%" stopColor={metricDef.color} stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border-default)" vertical={false} />
                    <XAxis dataKey="date" tickFormatter={fmtDay} tick={{ fontSize: 11, fill: 'var(--text-muted)' }} minTickGap={24} />
                    <YAxis
                      tickFormatter={metricDef.axis === 'pct' ? (v => nf1.format(v * 100) + '%') : (metricDef.axis === 'pos' ? fmtPosition : fmtCompact)}
                      tick={{ fontSize: 11, fill: 'var(--text-muted)' }} width={44}
                      reversed={!!metricDef.reversed}
                      domain={metricDef.reversed ? [1, 'dataMax'] : undefined}
                    />
                    <Tooltip content={<MainChartTooltip metricDef={metricDef} compare={compare} actionsByDate={groupMarkersByDate(actionMarkers(siteActions, mainChartData.map(p => p.date)))} />} />
                    {compare && (
                      <Line type="monotone" dataKey="prev" stroke="var(--text-muted)" strokeWidth={1.5} strokeDasharray="4 4" dot={false} isAnimationActive={false} connectNulls />
                    )}
                    <Area type="monotone" dataKey="value" stroke={metricDef.color} strokeWidth={2.5} fill="url(#gscMainFill)" dot={false} isAnimationActive={false} />
                    {actionMarkers(siteActions, mainChartData.map(p => p.date)).map(m => (
                      <ReferenceLine key={m.key} x={m.x} stroke={m.color} strokeDasharray="4 4"
                        label={{ value: m.emoji, position: 'top', fontSize: 12 }} />
                    ))}
                  </ComposedChart>
                </ResponsiveContainer>
              </Panel>
              {showQuickAction && (
                <QuickActionModal siteId={actionSiteId} siteName={actionSiteName} defaultChannel="gsc"
                  onClose={() => setShowQuickAction(false)}
                  onSaved={() => { setShowQuickAction(false); setActionsRefresh(x => x + 1); }} />
              )}

              <div className="ga-grid-2">
                <Panel title="Appareils" subtitle="répartition des clics">
                  <DonutChart data={report.devices} dataKey="clicks" />
                </Panel>
                <Panel title="Pays" subtitle="Top par clics">
                  {(!report.countries || report.countries.length === 0) ? <div className="ga-empty">Aucune donnée</div> : (
                    <ResponsiveContainer width="100%" height={260}>
                      <BarChart data={report.countries} layout="vertical" margin={{ top: 4, right: 24, left: 8, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--border-default)" horizontal={false} />
                        <XAxis type="number" tickFormatter={fmtCompact} tick={{ fontSize: 11, fill: 'var(--text-muted)' }} />
                        <YAxis type="category" dataKey="label" width={90} tick={{ fontSize: 11, fill: 'var(--text-primary)' }} />
                        <Tooltip formatter={(v) => fmtNumber(v)} contentStyle={{ background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 8, fontSize: 12 }} />
                        <Bar dataKey="clicks" fill="var(--color-accent)" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </Panel>
              </div>

              <Panel title="Apparence dans la recherche" subtitle="Types de résultats (extraits enrichis, vidéos, avis…)">
                <PerfTable rows={report.appearance} labelHead="Type d'affichage" empty="Aucun résultat enrichi détecté sur la période." />
              </Panel>
            </>
          )}

          {/* ── REQUÊTES ── */}
          {tab === 'queries' && (
            <Panel title="Top requêtes" subtitle="Les mots-clés qui amènent le plus de clics">
              <PerfTable rows={report.queries} labelHead="Requête" />
            </Panel>
          )}

          {/* ── PAGES ── */}
          {tab === 'pages' && (
            <Panel title="Top pages" subtitle="Les pages qui reçoivent le plus de clics">
              <PerfTable rows={report.pages} labelHead="Page" isPage />
            </Panel>
          )}

          {/* ── OPPORTUNITÉS ── */}
          {tab === 'opportunities' && (
            <>
              <Panel
                title="🎯 Sur le pas de la porte"
                subtitle="Requêtes en position 8 à 20 avec beaucoup d'impressions : un petit effort SEO peut les faire passer en page 1"
              >
                <p className="gsc-advice">
                  Ces requêtes vous voient déjà beaucoup (fortes impressions) mais vous restez juste sous la page 1.
                  Renforcer le contenu ciblant ces mots-clés (titre, intertitres, maillage interne) est le levier le plus rentable.
                </p>
                <PerfTable rows={report.opportunities?.strikingDistance} labelHead="Requête"
                  empty="Aucune requête en position 8–20 avec des impressions suffisantes sur la période." />
              </Panel>

              <Panel
                title="✍️ Fort potentiel, CTR faible"
                subtitle={`Pages bien classées (top 10) mais peu cliquées — CTR du site : ${fmtPercent2(report.opportunities?.siteCtr)}`}
              >
                <p className="gsc-advice">
                  Ces pages sont déjà bien positionnées mais leur taux de clic est inférieur à la moyenne du site.
                  Le problème vient souvent du <strong>titre</strong> ou de la <strong>méta-description</strong> : les réécrire pour les rendre plus attractifs peut augmenter les clics sans toucher au classement.
                </p>
                <PerfTable rows={report.opportunities?.lowCtrPages} labelHead="Page" isPage
                  empty="Aucune page en top 10 avec un CTR nettement sous la moyenne sur la période." />
              </Panel>
            </>
          )}
        </>
      )}
    </div>
  );
}

export default SearchConsole;
